import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { ActionPanels } from '../components/ActionPanels';
import { Tradesperson } from '@/lib/types';
import { debug } from '@/lib/utils';
import { TradeListContainer } from '../components/TradeListContainer';
import { ActionButtonType } from '../components/ActionButtons';
import { TradesPurpleHeader } from '../components/TradesPurpleHeader';
import { useAuth } from '@/contexts/AuthContext';

export default function LoggedInLandingPage() {
  const [activeButton, setActiveButton] = useState<ActionButtonType | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [tradespeopleCount, setTradespeopleCount] = useState(0);
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>([]);

  // Define handleButtonClick before useEffect to avoid temporal dead zone
  const handleButtonClick = (button: ActionButtonType) => {
    // If clicking the same button, toggle it off
    if (activeButton === button) {
      setActiveButton(null);
    } else {
      setActiveButton(button);
    }
  };

  // Set up custom event listener for the prominent Add Trade button
  useEffect(() => {
    const handleTradeAction = (event: CustomEvent<{action: ActionButtonType}>) => {
      if (event.detail && event.detail.action) {
        // If clicking the same button, toggle it off
        if (activeButton === event.detail.action) {
          setActiveButton(null);
        } else {
          setActiveButton(event.detail.action);
        }
      }
    };

    // Use EventListener instead of any
    window.addEventListener('tradespurple-action', handleTradeAction as EventListener);

    return () => {
      window.removeEventListener('tradespurple-action', handleTradeAction as EventListener);
    };
  }, [activeButton]);

  // Handle category filter change
  const handleCategoryChange = (category: string | null) => {
    setActiveFilter(category);
  };

  // Callback for when a tradesperson is added or removed
  const handleTradespersonDataChange = (trade: Tradesperson) => {
    debug.log('Tradesperson data changed, refreshing TradeList');
    // Increment the trigger to force the TradeList to refresh its data
    setRefreshTrigger(prev => prev + 1);
  };

  // Callback when trade list data is loaded
  // Use useCallback to create a stable function reference that doesn't change on every render
  const handleTradesLoaded = useCallback((trades: Tradesperson[]) => {
    setTradespeopleCount(trades.length);

    // Only update filteredTradespeople if the data actually changed
    // This prevents unnecessary re-renders and breaks the infinite loop
    if (trades.length === 0 && filteredTradespeople.length === 0) {
      // Don't update state if both are empty arrays
      return;
    }

    // Simple check to avoid unnecessary updates
    // Note: a deep equality check would be more accurate but also more expensive
    const tradesChanged = JSON.stringify(trades.map(t => t.id || t)) !==
                          JSON.stringify(filteredTradespeople.map(t => t.id || t));

    if (tradesChanged) {
      debug.log('LoggedInLandingPage: Filtered tradespeople updated', {
        count: trades.length,
        changed: true
      });
      setFilteredTradespeople(trades);
    } else {
      debug.log('LoggedInLandingPage: Skipping update (data unchanged)', {
        count: trades.length,
        changed: false
      });
    }
  }, [filteredTradespeople]);

  // After closing a panel, force refresh the list if we were adding/editing
  const handlePanelClose = () => {
    if (activeButton === 'add' || activeButton === 'quote') {
      // Trigger a refresh when closing these panels since they might modify data
      setRefreshTrigger(prev => prev + 1);
    }
    setActiveButton(null);
  };

  return (
    <div className="max-w-md md:max-w-2xl mx-auto flex flex-col min-h-[100vh]">
      <Helmet>
        <title>TradesPurple | Your Trusted Trades List</title>
        <meta name="description" content="Manage and share your trusted tradespeople with your neighbours." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <meta name="theme-color" content="#7e22ce" />
      </Helmet>

      <div className="flex flex-col">
        {/* Always show the header */}
        <div className="mb-2">
          <TradesPurpleHeader forceCompact={true} />
        </div>

        {/* Active Panel */}
        <ActionPanels
          activePanel={activeButton}
          onClose={handlePanelClose}
          onDataChange={handleTradespersonDataChange}
          filteredTradespeople={filteredTradespeople}
        />

        {/* Trade List Container - Hidden during SmartQuote and Add Trade modes */}
        {(activeButton !== 'quote' && activeButton !== 'add') && (
          <TradeListContainer
            key={`tradelist-${refreshTrigger}`} // Force re-mount when data changes
            mode="personal"
            dataSource="user"
            categoryFilter={activeFilter}
            onCategoryChange={handleCategoryChange}
            onTradespersonSaved={handleTradespersonDataChange}
            onDataLoaded={handleTradesLoaded}
            showHeader={false}
          />
        )}
      </div>
    </div>
  );
}
